package pl.zar.tikthrow.viewmodel

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.russhwolf.settings.Settings
import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.plugins.*
import io.ktor.client.plugins.contentnegotiation.*
import io.ktor.client.request.*
import io.ktor.client.statement.*
import io.ktor.http.HttpHeaders
import io.ktor.http.HttpMethod
import io.ktor.http.HttpStatusCode
import io.ktor.http.contentType
import io.ktor.http.isSuccess
import io.ktor.http.ContentType as KtorContentType
import io.ktor.serialization.kotlinx.json.*
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch
import kotlinx.serialization.json.Json
import pl.zar.tikthrow.shared.domain.User
import pl.zar.tikthrow.shared.dto.AuthResponse
import pl.zar.tikthrow.shared.dto.LoginRequest
import pl.zar.tikthrow.shared.dto.RegisterRequest
import pl.zar.tikthrow.util.AUTH_TOKEN_KEY
import pl.zar.tikthrow.util.apiBaseUrl
import pl.zar.tikthrow.util.createPostsSettings
import pl.zar.tikthrow.util.userFacingNetworkError

sealed class AuthState {
    data object Loading : AuthState()
    data class LoggedIn(val user: User, val token: String) : AuthState()
    data class LoggedOut(val error: String? = null) : AuthState()
}

class AuthViewModel : ViewModel() {

    private val settings: Settings? = runCatching { createPostsSettings() }.getOrNull()
    private var storedToken: String? = null

    private val client = HttpClient {
        install(ContentNegotiation) {
            json(Json { ignoreUnknownKeys = true; isLenient = true })
        }
        install(HttpTimeout) {
            connectTimeoutMillis = 30_000L
            requestTimeoutMillis = 60_000L
            socketTimeoutMillis = 60_000L
        }
    }

    private val _state = MutableStateFlow<AuthState>(AuthState.Loading)
    val state: StateFlow<AuthState> = _state.asStateFlow()

    init {
        checkStoredToken()
    }

    private fun checkStoredToken() {
        viewModelScope.launch {
            val token = settings?.getStringOrNull(TOKEN_KEY)
            println("[AUTH] checkStoredToken: token=${if (token != null) "znaleziony (${token.take(8)}...)" else "brak"}")
            if (token == null) {
                _state.value = AuthState.LoggedOut()
                return@launch
            }
            storedToken = token
            try {
                println("[AUTH] GET $apiBaseUrl/api/auth/me")
                val response: HttpResponse = client.request("$apiBaseUrl/api/auth/me") {
                    method = HttpMethod.Get
                    header(HttpHeaders.Authorization, "Bearer $token")
                }
                println("[AUTH] /me -> HTTP ${response.status.value}")
                if (response.status.isSuccess()) {
                    val user = response.body<User>()
                    println("[AUTH] auto-login OK: ${user.email}")
                    _state.value = AuthState.LoggedIn(user, token)
                } else {
                    val body = runCatching { response.bodyAsText() }.getOrElse { "" }
                    println("[AUTH] /me nieudane: $body")
                    if (response.status == HttpStatusCode.Unauthorized || response.status == HttpStatusCode.Forbidden) {
                        clearToken()
                    }
                    _state.value = AuthState.LoggedOut()
                }
            } catch (e: Exception) {
                println("[AUTH] ${userFacingNetworkError("Sprawdzenie sesji", e)}")
                _state.value = AuthState.LoggedOut()
            }
        }
    }

    fun login(email: String, password: String) {
        viewModelScope.launch {
            _state.value = AuthState.Loading
            println("[AUTH] login: email=${email.trim()}, url=$apiBaseUrl/api/auth/login")
            try {
                val response: HttpResponse = client.request("$apiBaseUrl/api/auth/login") {
                    method = HttpMethod.Post
                    contentType(KtorContentType.Application.Json)
                    setBody(LoginRequest(email.trim(), password))
                }
                val body = runCatching { response.bodyAsText() }.getOrElse { "" }
                println("[AUTH] login -> HTTP ${response.status.value}: $body")
                if (response.status.isSuccess()) {
                    val auth = response.body<AuthResponse>()
                    persistToken(auth.token)
                    println("[AUTH] login OK: user=${auth.user.email}, token=${auth.token.take(8)}...")
                    _state.value = AuthState.LoggedIn(auth.user, auth.token)
                } else {
                    _state.value = AuthState.LoggedOut(error = body.ifBlank { "HTTP ${response.status.value}" })
                }
            } catch (e: Exception) {
                val error = userFacingNetworkError("Logowanie", e)
                println("[AUTH] $error")
                _state.value = AuthState.LoggedOut(error = error)
            }
        }
    }

    fun register(email: String, password: String, displayName: String) {
        viewModelScope.launch {
            _state.value = AuthState.Loading
            println("[AUTH] register: email=${email.trim()}, displayName=${displayName.trim()}, url=$apiBaseUrl/api/auth/register")
            try {
                val response: HttpResponse = client.request("$apiBaseUrl/api/auth/register") {
                    method = HttpMethod.Post
                    contentType(KtorContentType.Application.Json)
                    setBody(RegisterRequest(email.trim(), password, displayName.trim()))
                }
                val body = runCatching { response.bodyAsText() }.getOrElse { "" }
                println("[AUTH] register -> HTTP ${response.status.value}: $body")
                if (response.status.isSuccess()) {
                    val auth = response.body<AuthResponse>()
                    persistToken(auth.token)
                    println("[AUTH] register OK: user=${auth.user.email}, token=${auth.token.take(8)}...")
                    _state.value = AuthState.LoggedIn(auth.user, auth.token)
                } else {
                    _state.value = AuthState.LoggedOut(error = body.ifBlank { "HTTP ${response.status.value}" })
                }
            } catch (e: Exception) {
                val error = userFacingNetworkError("Rejestracja", e)
                println("[AUTH] $error")
                _state.value = AuthState.LoggedOut(error = error)
            }
        }
    }

    fun logout() {
        viewModelScope.launch {
            val token = storedToken
            if (token != null) {
                try {
                    client.request("$apiBaseUrl/api/auth/logout") {
                        method = HttpMethod.Post
                        header(HttpHeaders.Authorization, "Bearer $token")
                    }
                } catch (_: Exception) {}
            }
            clearToken()
            _state.value = AuthState.LoggedOut()
        }
    }

    private fun persistToken(token: String) {
        storedToken = token
        settings?.putString(TOKEN_KEY, token)
    }

    private fun clearToken() {
        storedToken = null
        settings?.remove(TOKEN_KEY)
    }

    companion object {
        private const val TOKEN_KEY = AUTH_TOKEN_KEY
    }
}
